// OwnershipScreen.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { View, Text, Pressable, StyleSheet, Platform } from "react-native";
import { AppContext } from "../../../App";
import { useFormNavigation } from "../../utils/navigationUtils";
import Header from "../../components/Header";
import { useTranslation } from "../../i18n";

const COLORS = {
  primary: "#14B8A6",
  secondary: "#7C3AED",
  background: "#F7F9FA",
  card: "#FFFFFF",
  textDark: "#1C1C1E",
  textMuted: "#6B7280",
};

function isBackOfficeEmbeddedRoute() {
  try {
    if (Platform.OS !== "web") return false;

    const params = new URLSearchParams(window.location.search);

    return (
      params.get("mode") === "backoffice" ||
      params.get("source") === "backoffice" ||
      params.get("embedded") === "backoffice"
    );
  } catch (error) {
    return false;
  }
}

export default function OwnershipScreen({ navigation, route }) {
  const { setFormProgress, verification, setVerification } = useContext(AppContext);
  const { updateCurrentStep } = useFormNavigation();
  const { t } = useTranslation();

  const isBackOfficeMode = useMemo(() => {
    return (
      route?.params?.mode === "backoffice" ||
      route?.params?.source === "backoffice" ||
      route?.params?.embedded === "backoffice" ||
      isBackOfficeEmbeddedRoute()
    );
  }, [route?.params]);

  useEffect(() => {
    setFormProgress((prev) => {
      if (prev?.currentStep === "ownership_screen") return prev;
      return { ...prev, currentStep: "ownership_screen" };
    });

    // Optional: inform the back-office iframe parent which screen is currently displayed
    if (Platform.OS === "web" && isBackOfficeMode && window?.parent) {
      window.parent.postMessage(
        {
          type: "TAXI_ORIGINAL_PLATFORM_SCREEN",
          currentStep: "ownership_screen",
        },
        "*"
      );
    }
  }, [setFormProgress, isBackOfficeMode]);

  const moveToOriginalNextScreen = (isOwner) => {
    const nextScreen = isOwner
      ? "vehicle_owner_form_screen"
      : "not_vehicle_owner_form_screen";

    setFormProgress((prev) => ({
      ...prev,
      completedSteps: [
        ...new Set([...(prev?.completedSteps || []), "ownership_screen"]),
      ],
      currentStep: nextScreen,
    }));

    setVerification((prev) => ({
      ...prev,
      actorRole: isOwner ? "OWNER" : "DRIVER",
    }));

    navigation.navigate(nextScreen);
  };

  const handleOwnerSelection = (isOwner) => {
    moveToOriginalNextScreen(isOwner);
  };

  return (
    <View style={styles.screen}>
      <Header />

      <Pressable
        style={styles.backButton}
        onPress={() => navigation.navigate("home_screen")}
      >
        <Text style={styles.backIcon}>←</Text>
      </Pressable>

      <View style={styles.content}>
        <Text style={styles.title}>
          {t("ownership.title")}
        </Text>

        <Text style={styles.subtitle}>
          {t("ownership.subtitle")}
        </Text>

        {isBackOfficeMode && (
          <View style={styles.backOfficeBadge}>
            <Text style={styles.backOfficeBadgeText}>
              Mode back-office activé
            </Text>
          </View>
        )}

        <View style={styles.choiceContainer}>
          <Pressable
            onPress={() => handleOwnerSelection(true)}
            style={({ pressed }) => [
              styles.choiceCard,
              styles.choiceYes,
              pressed && styles.pressed,
            ]}
          >
            <Text style={styles.choiceTitle}>{t("common.yes")}</Text>
            <Text style={styles.choiceDescription}>
              {t("ownership.yesDescription")}
            </Text>
          </Pressable>

          <Pressable
            onPress={() => handleOwnerSelection(false)}
            style={({ pressed }) => [
              styles.choiceCard,
              styles.choiceNo,
              pressed && styles.pressed,
            ]}
          >
            <Text style={styles.choiceTitle}>{t("common.no")}</Text>
            <Text style={styles.choiceDescription}>
              {t("ownership.noDescription")}
            </Text>
          </Pressable>
        </View>

        <Text style={styles.helperText}>{t("ownership.helperText")}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: COLORS.background,
    paddingHorizontal: 16,
  },
  content: {
    width: "100%",
    maxWidth: 520,
    alignSelf: "center",
  },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    marginTop: 16,
  },
  backIcon: {
    fontSize: 18,
    color: COLORS.textDark,
  },
  title: {
    fontSize: 24,
    fontWeight: "800",
    color: COLORS.textDark,
    textAlign: "center",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: COLORS.textMuted,
    textAlign: "center",
    marginBottom: 16,
  },
  backOfficeBadge: {
    alignSelf: "center",
    backgroundColor: "#ECFDF5",
    borderColor: "#14B8A6",
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    marginBottom: 24,
  },
  backOfficeBadgeText: {
    color: "#0F766E",
    fontSize: 12,
    fontWeight: "700",
  },
  choiceContainer: {
    gap: 16,
  },
  choiceCard: {
    backgroundColor: COLORS.card,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 3,
  },
  choiceYes: {
    borderLeftWidth: 5,
    borderLeftColor: COLORS.secondary,
  },
  choiceNo: {
    borderLeftWidth: 5,
    borderLeftColor: COLORS.primary,
  },
  pressed: {
    transform: [{ scale: 0.98 }],
  },
  choiceTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: COLORS.textDark,
    marginBottom: 4,
  },
  choiceDescription: {
    fontSize: 14,
    color: COLORS.textMuted,
    lineHeight: 20,
  },
  helperText: {
    marginTop: 24,
    fontSize: 13,
    color: COLORS.textMuted,
    textAlign: "center",
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalContainer: {
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: 20,
    padding: 24,
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 8,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "800",
    color: COLORS.textDark,
    marginBottom: 16,
    textAlign: "center",
  },
  modalMessage: {
    fontSize: 15,
    color: COLORS.textMuted,
    lineHeight: 22,
    marginBottom: 12,
    textAlign: "left",
  },
  modalButton: {
    marginTop: 16,
    backgroundColor: COLORS.primary,
    paddingVertical: 14,
    borderRadius: 14,
    alignItems: "center",
  },
  modalButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
  },
});